type ErrorVariant = 'page' | 'banner' | 'field';

interface ErrorMessageProps {
  message?: string;
  /** Only applies to the default `page` variant. */
  fullPage?: boolean;
  /** `page` (centered block, default), `banner` (form-level alert box), `field` (compact text under an input). */
  variant?: ErrorVariant;
  /** Element id — pair with an input's `aria-describedby` for `field` errors. */
  id?: string;
  className?: string;
}

const AlertIcon = ({ className }: { className: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth={2}
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
    />
  </svg>
);

/**
 * Inline error display. Use it when something on the page failed to load, or
 * when a form input needs correcting — the failure of an action the user
 * triggered belongs in a toast instead (`showToast.error`, src/lib/toast.ts).
 * The rule is written out in docs/product_website/ui-design.md.
 *
 * Every variant announces to screen readers: an inline error that is only a
 * color change is invisible to anyone not looking at that part of the page.
 */
export const ErrorMessage = ({
  message = 'Something went wrong.',
  fullPage = false,
  variant = 'page',
  id,
  className = '',
}: ErrorMessageProps) => {
  if (variant === 'banner') {
    return (
      <div
        id={id}
        role="alert"
        aria-live="assertive"
        className={`flex items-start gap-2.5 p-4 rounded-xl bg-red-50 border border-red-200 ${className}`}
      >
        <AlertIcon className="w-5 h-5 flex-shrink-0 text-red-700 mt-px" />
        <p className="text-sm text-red-800 font-medium">{message}</p>
      </div>
    );
  }

  if (variant === 'field') {
    return (
      <p id={id} role="alert" className={`text-sm text-red-600 ${className}`}>
        {message}
      </p>
    );
  }

  const wrapper = fullPage ? 'min-h-screen' : 'min-h-[200px]';
  return (
    <div
      role="alert"
      aria-live="polite"
      className={`flex flex-col items-center justify-center gap-3 text-center px-6 ${wrapper} ${className}`}
    >
      <AlertIcon className="w-8 h-8 text-red-600" />
      <p className="text-sm text-gray-700 max-w-md">{message}</p>
    </div>
  );
};
